// Wires the chat attachment data hooks to the presentational cards in
// ui/primitives/attachment_cards.tsx, one card per attachment kind.
import { type ReactNode, useState } from 'react';

import { useOpenAttachmentViewer } from '@apps/web/features/phoebe_chat_v3/ui/attachment_viewer';
import {
    useAttachmentBlob,
    useAttachmentDownload,
    useAttachmentPreview,
    useBlobObjectUrl,
} from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_data';
import {
    PdfPageCanvas,
    type PdfRenderState,
    usePdfDocument,
} from '@apps/web/features/phoebe_chat_v3/ui/pdf_page_canvas';
import {
    AttachmentCard,
    AttachmentImageCard,
    AttachmentPreviewLoadingBody,
    attachmentShapeLine,
    EntryListPreviewBody,
    fileTypeForFilename,
    MarkdownPreviewBody,
    PdfPreviewBody,
    TablePreviewBody,
    TextPreviewBody,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/attachment_cards';
import {
    type AttachmentPreviewRead,
    type Mode,
} from '@libraries/typescript/api_client';

export type V3PresentationContext = {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly organizationTimezone: string;
};

export type V3ChatAttachment = {
    readonly kind: 'chat-attachment';
    readonly version: 1;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filepath: string;
    readonly filename: string;
    readonly contentType: string;
    readonly disposition: 'inline' | 'attachment';
    readonly sizeBytes: number;
};

function isImageAttachment(attachment: V3ChatAttachment): boolean {
    return (
        attachment.disposition === 'inline' &&
        attachment.contentType.toLowerCase().startsWith('image/')
    );
}

// PDFs render from their own bytes rather than the preview endpoint, and
// every attachment but an image is served as application/octet-stream, so
// the filename is the only signal.
function isPdfAttachment(attachment: V3ChatAttachment): boolean {
    return fileTypeForFilename(attachment.filename).kind === 'pdf';
}

type ConnectedCardProps = {
    readonly attachment: V3ChatAttachment;
    readonly organizationId: string;
    readonly mode: Mode;
};

function useAttachmentCardProps({
    attachment,
    organizationId,
    mode,
}: ConnectedCardProps) {
    const openViewer = useOpenAttachmentViewer();
    const { isDownloading, downloadError, startDownload } =
        useAttachmentDownload({
            organizationId,
            mode,
            runId: attachment.runId,
            attachmentId: attachment.attachmentId,
            filename: attachment.filename,
        });
    return {
        filename: attachment.filename,
        sizeBytes: attachment.sizeBytes,
        isDownloading,
        downloadError,
        onDownload: startDownload,
        onOpen: () =>
            openViewer({
                organizationId,
                mode,
                runId: attachment.runId,
                attachmentId: attachment.attachmentId,
            }),
    };
}

function previewBody(
    filename: string,
    preview: AttachmentPreviewRead | null,
    failed: boolean,
): ReactNode {
    if (failed) {
        return undefined;
    }
    if (preview === null) {
        return <AttachmentPreviewLoadingBody filename={filename} />;
    }
    const body = preview.preview;
    switch (body.kind) {
        case 'text':
            if (body.text === '') {
                return undefined;
            }
            return body.text_format === 'markdown' ? (
                <MarkdownPreviewBody markdown={body.text} />
            ) : (
                <TextPreviewBody text={body.text} />
            );
        case 'table':
            if (body.columns.length === 0) {
                return undefined;
            }
            return <TablePreviewBody columns={body.columns} rows={body.rows} />;
        case 'archive':
            if (body.entries.length === 0) {
                return undefined;
            }
            return (
                <EntryListPreviewBody
                    entries={body.entries}
                    entryCount={body.entry_count}
                />
            );
        default:
            return undefined;
    }
}

function ConnectedPreviewCard(props: ConnectedCardProps) {
    const { attachment, organizationId, mode } = props;
    const { preview, failed } = useAttachmentPreview({
        organizationId,
        mode,
        runId: attachment.runId,
        attachmentId: attachment.attachmentId,
        full: false,
    });
    const cardProps = useAttachmentCardProps(props);
    return (
        <AttachmentCard
            {...cardProps}
            shapeLine={
                preview === null ? undefined : attachmentShapeLine(preview)
            }
        >
            {previewBody(attachment.filename, preview, failed)}
        </AttachmentCard>
    );
}

function ConnectedPdfCard(props: ConnectedCardProps) {
    const { attachment, organizationId, mode } = props;
    const { blob, failed: blobFailed } = useAttachmentBlob({
        organizationId,
        mode,
        runId: attachment.runId,
        attachmentId: attachment.attachmentId,
    });
    const { pdf, failed: pdfFailed } = usePdfDocument(blob);
    const [renderState, setRenderState] = useState<PdfRenderState | null>(null);
    const cardProps = useAttachmentCardProps(props);
    if (blobFailed || pdfFailed || renderState === 'failed') {
        return <AttachmentCard {...cardProps} />;
    }
    return (
        <AttachmentCard {...cardProps}>
            <PdfPreviewBody
                filename={attachment.filename}
                isRendering={renderState !== 'rendered'}
            >
                {pdf !== null && (
                    <PdfPageCanvas
                        pdf={pdf}
                        pageNumber={1}
                        label={`First page of ${attachment.filename}`}
                        onRenderStateChange={setRenderState}
                    />
                )}
            </PdfPreviewBody>
        </AttachmentCard>
    );
}

function ConnectedImageCard(props: ConnectedCardProps) {
    const { attachment, organizationId, mode } = props;
    // Fetch the image bytes through the api client (X-Mode + cookies) and
    // hand the <img> a blob URL. A cross-origin `<img src>` request would
    // otherwise be missing the mode header and the endpoint would fail
    // closed with 422.
    const { blob, failed } = useAttachmentBlob({
        organizationId,
        mode,
        runId: attachment.runId,
        attachmentId: attachment.attachmentId,
    });
    const blobUrl = useBlobObjectUrl(blob);
    const cardProps = useAttachmentCardProps(props);
    if (failed) {
        return <AttachmentCard {...cardProps} />;
    }
    return <AttachmentImageCard {...cardProps} imageUrl={blobUrl ?? null} />;
}

export function V3ChatAttachmentCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    const connected = {
        attachment,
        organizationId: context.organizationId,
        mode: context.mode,
    };
    if (isImageAttachment(attachment)) {
        return <ConnectedImageCard {...connected} />;
    }
    if (isPdfAttachment(attachment)) {
        return <ConnectedPdfCard {...connected} />;
    }
    return <ConnectedPreviewCard {...connected} />;
}
